import { Dialog, Transition, TransitionChild } from "@headlessui/react";
import React, { Fragment } from "react";
import Button from "./Button";
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useDismissOnEscape } from "./useDismissOnEscape";
interface DialogBoxProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactElement;
    panelClassName?: string;
    panelStyle?: React.CSSProperties;
    /**
     * Use for full-height panels that scroll internally. Prevents the overlay from
     * capturing vertical touch/scroll on mobile (nested scroll fights).
     */
    lockOverlayScroll?: boolean;
    /** Tighter title bar padding and typography (e.g. data-heavy modals). */
    compactChrome?: boolean;
    /** Appended on the same line as the title (e.g. party code · name), wraps with long text. */
    headerExtra?: React.ReactNode;
    /** Right side of the title bar, separated from the title and just before close. */
    headerAside?: React.ReactNode;
    /** Render panel edge-to-edge without outer viewport padding. */
    fullScreen?: boolean;
    /** Element focused when the dialog opens. */
    initialFocus?: React.MutableRefObject<HTMLElement | null>;
}

const DialogBox: React.FC<DialogBoxProps> = ({
    isOpen,
    onClose,
    title,
    children,
    panelClassName = '',
    panelStyle,
    lockOverlayScroll = false,
    compactChrome = false,
    headerExtra,
    headerAside,
    fullScreen = false,
    initialFocus,
}) => {
    const dismissRef = useDismissOnEscape(isOpen, onClose);
    return (
    <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={onClose} initialFocus={initialFocus}>
            <Transition.Child
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="fixed inset-0 bg-black bg-opacity-25 z-[9998]" />
            </Transition.Child>

            <div
                className={`fixed inset-0 z-[9999] ${lockOverlayScroll ? 'overflow-hidden' : 'overflow-y-auto'}`}
            >
                <div
                    className={`flex min-h-[100dvh] text-center ${
                        fullScreen ? 'items-stretch justify-stretch p-0' : 'items-center justify-center p-3 sm:p-4'
                    }`}
                >
                    <Transition.Child

                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100" 
                        leaveTo="opacity-0 scale-95"
                    >
                        <Dialog.Panel
                            ref={dismissRef}
                            className={`w-auto max-w-full transform overflow-hidden rounded-lg bg-white p-0.5 text-left align-middle shadow-xl transition-all touch-auto ${panelClassName}`}
                            style={panelStyle}
                        >
                            <div
                                className={`flex shrink-0 justify-between gap-1 bg-gray-200 items-center border-b ${
                                    compactChrome ? 'py-0 px-1' : 'p-0.5'
                                }`}
                            >
                                <div className="min-w-0 flex-1 pr-1 text-left">
                                    <Dialog.Title
                                        as="h3"
                                        className={`flex min-w-0 flex-wrap items-baseline gap-x-1 font-medium leading-tight text-gray-900 ${
                                            compactChrome ? 'text-xs py-0.5 pl-0.5' : 'text-sm'
                                        }`}
                                    >
                                        <span className="min-w-0">{title}</span>
                                        {headerExtra ? (
                                            <>
                                                <span className="shrink-0 text-gray-500" aria-hidden>
                                                    ·
                                                </span>
                                                <span
                                                    className={`min-w-0 text-gray-900 ${
                                                        compactChrome ? 'text-[11px]' : ''
                                                    }`}
                                                >
                                                    {headerExtra}
                                                </span>
                                            </>
                                        ) : null}
                                    </Dialog.Title>
                                </div>
                                {headerAside ? (
                                    <div className={`shrink-0 ${compactChrome ? 'pr-1' : 'pr-1.5'}`}>
                                        {headerAside}
                                    </div>
                                ) : null}
                                <Button
                                    onClick={onClose}
                                    className={`shrink-0 bg-gray-200 text-gray-900 hover:text-gray-700 ${
                                        compactChrome ? 'p-0' : 'p-0.5'
                                    }`}
                                    aria-label="Close"
                                >
                                    <XMarkIcon className={`text-gray-900 ${compactChrome ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} />
                                </Button>
                            </div>
                            {children}
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </div>
        </Dialog>
    </Transition>
    );
};


export default DialogBox;
